"use client";

import React, { useRef } from "react";
import Link from "next/link";
import { FaHome, FaCaretRight } from "react-icons/fa";

type TBreadCrumbProps = {
  listClasses?: string;
  activeClasses?: string;
  capitalizeLinks?: boolean;
  path?: { key: string; name: string }[];
};

const DynamicBreadcrumb = ({
  listClasses = "",
  activeClasses = "",
  capitalizeLinks = false,
  path = [],
}: TBreadCrumbProps) => {
  const isDown = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const sliderRef = useRef<HTMLDivElement>(null);

  const handlePointerDown = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    if (!sliderRef.current) return;
    isDown.current = true;
    sliderRef.current.classList.add("active");

    const clientX = "touches" in e ? e.touches[0].pageX : e.pageX;
    startX.current = clientX - sliderRef.current.offsetLeft;
    scrollLeft.current = sliderRef.current.scrollLeft;
  };

  const handlePointerMove = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    if (!isDown.current || !sliderRef.current) return;
    e.preventDefault();
    const clientX = "touches" in e ? e.touches[0].pageX : e.pageX;
    const walk = (clientX - sliderRef.current.offsetLeft - startX.current) * 3;
    sliderRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const handlePointerUp = () => {
    isDown.current = false;
    sliderRef.current?.classList.remove("active");
  };

  return (
    path.length > 0 && (
      <nav
        role="navigation"
        aria-label="breadcrumb"
        className="bgHero bg-heroBg bg-cover"
      >
        <div
          ref={sliderRef}
          className="container mx-auto flex items-center space-x-4 p-4 overflow-x-hidden md:overflow-x-visible select-none transition-transform transform scale-98 active:bg-opacity-30 active:cursor-grabbing active:transform scale-100"
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
        >
          <ul className="flex items-center mx-2 justify-end md:justify-center">
            <li className={listClasses}>
              <Link href="/" aria-label="Home">
                <FaHome className="text-white fill-white" />
              </Link>
            </li>
            {path.reduce((acc: JSX.Element[], item, index) => {
              const newPath = `/${path
                .slice(0, index + 1)
                .map((p) => p.key)
                .join("/")}`;

              acc.push(
                <React.Fragment key={item.key}>
                  <span className="separator mx-2">
                    <FaCaretRight className="text-white fill-white" />
                  </span>
                  <li>
                    <Link
                      href={newPath}
                      className={`${
                        index === path.length - 1
                          ? `${listClasses} ${activeClasses}`
                          : listClasses
                      }`}
                    >
                      {capitalizeLinks ? item.name.toUpperCase() : item.name}
                    </Link>
                  </li>
                </React.Fragment>
              );
              return acc;
            }, [])}
          </ul>
        </div>
      </nav>
    )
  );
};

export default DynamicBreadcrumb;
